import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Form as FormComponent } from "@/components/Form";
import { Input } from "@/components/ui/input";
import { CirclePlus, ImageUp, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { toast } from "sonner";
import { registerUserTenantData } from "@/Api/usersTenant";
import { getSelectRoles } from "@/Api/roles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/auth";
import { insertUser } from "@/Api/user";

export const AddUserBtn = ({ updateFront, tenantUuid }) => {
  const form = useForm();
  const formWatch = form.watch();

  const [isLoad, setIsLoad] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const userData = useAuth();
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const fetchRoles = async () => {
    try {
      const { data } = await getSelectRoles();
      setRoles(data?.response || []);
    } catch (error) {
      console.error("Erro ao buscar unidades:", error);
    }
  };
  const formSubmit = async (data) => {
    setIsLoad((prev) => !prev);
    const roles: number[] = [];
    if (data?.role_id) {
      roles.push(Number(data?.role_id));
    }
    const payload = {
      ...data,
      photo: data?.photo?.[0] || null,
      roles: roles,
    };
    const userAdminTenant = userData?.user?.user_type_id == 2;
    let endpoint = userAdminTenant ? insertUser : registerUserTenantData;
    let payloadSend = userAdminTenant
      ? payload
      : { payload, tenant_uuid: tenantUuid };
    const { data: apiResponse, ok } = await endpoint(payloadSend);
    if (!ok) {
      toast("Error ao registrar o usuário!");
      setIsLoad((prev) => !prev);
      setIsOpen((prev) => !prev);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    toast(apiResponse?.message);
    setIsLoad((prev) => !prev);
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) form.reset();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Usuário
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        {/* <ScrollArea> */}
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Usuário
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-3" onSubmit={form.handleSubmit(formSubmit)}>
            <FormItem className="flex flex-col items-center gap-1">
              <FormLabel className={cn("flex gap-2")}>Perfil:</FormLabel>
              <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                <div className="flex text-sm/6 text-gray-600 h-full w-full">
                  <label
                    htmlFor="file-upload"
                    className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                  >
                    {formWatch?.photo?.length > 0 && (
                      <img
                        className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                        src={URL.createObjectURL(formWatch?.photo?.[0])}
                        alt="profile_image"
                      />
                    )}
                    <span className="flex gap-2 items-center relative z-50">
                      {!(formWatch?.photo?.length > 0) ? (
                        <ImageUp className="size-7 group-hover:text-blue-700" />
                      ) : (
                        <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                      )}
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      {...form.register("photo")}
                    />
                  </label>
                </div>
              </div>
            </FormItem>
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Campo Obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nome:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Insira o nome" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: "Campo Obrigatório",
                pattern: { value: /^\S+@\S+$/i, message: "Formato inválido." },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    E-mail:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Insira o e-mail" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              rules={{ required: "Campo Obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Senha:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="password"
                      placeholder="Digite a senha"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormComponent.Field name="role_id">
              {({ field }) => (
                <FormComponent.Item>
                  <FormComponent.Label>
                    Cargo <FormComponent.Message />
                  </FormComponent.Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormComponent.Control>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um Cargo" />
                      </SelectTrigger>
                    </FormComponent.Control>
                    <SelectContent>
                      {roles.map((item: any) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormComponent.Item>
              )}
            </FormComponent.Field>
            <DialogFooter className="mt-5">
              <Button
                type="submit"
                className={cn("flex gap-2 mt-1", colors.bg_primary)}
                disabled={isLoad}
              >
                {isLoad ? (
                  <>
                    <Loader className="animate-spin size-4" /> Adicionando
                  </>
                ) : (
                  <>Adicionar</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        {/* </ScrollArea> */}
      </DialogContent>
    </Dialog>
    // rm -rm .git
  );
};
